/**
 * AdminBugReports — kullanıcı sorun bildirimlerini inceleme.
 * Route: /admin/bug-reports · Roles: admin, superadmin
 */
import { useState } from 'react';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, LoadingState } from '../components/admin';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { useAdminBugReports, useUpdateBugReportStatus } from '../api/bugReports';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bug, CheckCircle, RotateCcw, ExternalLink, User, Clock, ImageOff } from 'lucide-react';

const FILTERS = [
  { value: 'open', tr: 'Açık', en: 'Open' },
  { value: 'resolved', tr: 'Çözüldü', en: 'Resolved' },
  { value: '', tr: 'Tümü', en: 'All' },
];

export default function AdminBugReports() {
  const { lang } = useLanguage();
  const toast = useToast();
  const T = (tr, en) => (lang === 'tr' ? tr : en);

  const [filter, setFilter] = useState('open');
  const [zoom, setZoom] = useState(null); // büyütülen screenshot url

  const { data, isLoading } = useAdminBugReports(filter || undefined);
  const updateStatus = useUpdateBugReportStatus();

  const items = data?.items ?? [];

  const toggle = async (r) => {
    const next = r.status === 'open' ? 'resolved' : 'open';
    try {
      await updateStatus.mutateAsync({ id: r.report_id, status: next });
      toast.success(T('Durum güncellendi', 'Status updated'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: T('Sistem & Güvenlik', 'System & Security') },
          { label: T('Sorun Bildirimleri', 'Bug Reports') },
        ]}
        title={T('Sorun Bildirimleri', 'Bug Reports')}
        subtitle={
          data?.open_count != null
            ? T(`${data.open_count} açık bildirim`, `${data.open_count} open reports`)
            : ''
        }
      />

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
              filter === f.value
                ? 'bg-[var(--brand-primary)] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {T(f.tr, f.en)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState />
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 flex flex-col items-center gap-2">
          <Bug size={40} />
          <p className="text-sm">{T('Bildirim yok', 'No reports')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {items.map((r) => (
            <div key={r.report_id} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                  <span className="flex items-center gap-1"><User size={13} /> {r.user_name || r.user_email || '—'}</span>
                  <span className="flex items-center gap-1"><Clock size={13} /> {fmtDate(r.created_at)}</span>
                  {r.page_url && (
                    <span className="flex items-center gap-1 font-mono text-[11px] bg-gray-50 px-2 py-0.5 rounded border border-gray-200">
                      <ExternalLink size={11} /> {r.page_url}
                    </span>
                  )}
                </div>
                <Badge variant={r.status === 'resolved' ? 'secondary' : 'default'}>
                  {r.status === 'resolved' ? T('Çözüldü', 'Resolved') : T('Açık', 'Open')}
                </Badge>
              </div>

              <p className="text-sm text-gray-800 whitespace-pre-wrap mb-3">{r.message}</p>

              <div className="flex items-end justify-between gap-3 flex-wrap">
                {r.screenshot_url ? (
                  <button
                    onClick={() => setZoom(r.screenshot_url)}
                    className="block rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-sky-400"
                  >
                    <img src={r.screenshot_url} alt="screenshot" className="h-24 w-auto object-cover" />
                  </button>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-gray-300"><ImageOff size={14} /> {T('Ekran görüntüsü yok', 'No screenshot')}</span>
                )}

                <Button
                  variant={r.status === 'open' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggle(r)}
                  disabled={updateStatus.isPending}
                >
                  {r.status === 'open' ? (
                    <><CheckCircle size={15} className="mr-1" /> {T('Çözüldü işaretle', 'Mark resolved')}</>
                  ) : (
                    <><RotateCcw size={15} className="mr-1" /> {T('Tekrar aç', 'Reopen')}</>
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Screenshot zoom overlay */}
      {zoom && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6 cursor-zoom-out"
          onClick={() => setZoom(null)}
        >
          <img src={zoom} alt="screenshot" className="max-h-full max-w-full rounded-lg" />
        </div>
      )}
    </AdminLayout>
  );
}
